import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `categoryGrid` block.
 *
 * Decomposed from the inline 16-card category grid in
 * `src/pages/HelpCenter.tsx`. Each category surfaces an icon, title,
 * description, and Ruby chat prompt. The block itself is presentational
 * + click-handled; the page wires the chat-modal opener via the
 * `onCategoryClick` prop.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `category-grid.tsx` / `category-grid.payload.ts`
 *   React component export:  `CategoryGrid`
 *   Payload `slug`:          `categoryGrid`
 *   Payload `interfaceName`: `CategoryGridBlock`
 */

export const CategoryGridBlock: BlockConfig = {
  slug: 'categoryGrid',
  interfaceName: 'CategoryGridBlock',
  labels: {
    singular: 'Category Grid',
    plural: 'Category Grid Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
    },
    {
      name: 'headline',
      type: 'text',
      label: 'Headline',
    },
    {
      name: 'subheadline',
      type: 'text',
      label: 'Subheadline',
      defaultValue: 'Click a topic to chat with Ruby about it',
    },
    {
      name: 'columns',
      type: 'select',
      defaultValue: '3',
      label: 'Columns',
      options: [
        { label: '2', value: '2' },
        { label: '3', value: '3' },
        { label: '4', value: '4' },
      ],
    },
    {
      name: 'categories',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 30,
      label: 'Categories',
      labels: { singular: 'Category', plural: 'Categories' },
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
            required: true,
          }),
          admin: {
            description:
              'Allowed: BadgeCheck, Crown, Eye, Gift, Lock, Rocket, Settings, Share2, Shield, ShoppingBag, Sparkles, TrendingUp, Trophy, User, Users, Wallet.',
          },
        },
        { name: 'title', type: 'text', required: true, label: 'Title' },
        {
          name: 'description',
          type: 'text',
          required: true,
          label: 'Description',
        },
        {
          name: 'prompt',
          type: 'textarea',
          required: true,
          label: 'Chat prompt',
          admin: {
            rows: 3,
            description:
              'Pre-filled message sent to the Ruby chat when the card is clicked.',
          },
        },
      ],
    },
  ],
};

export default CategoryGridBlock;
